import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { GeoJSON } from 'geojson';
import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';
import { ProductVisibilityMatrix, ProductKey, ProductVisibility } from 'src/shared/types/engine/products';
import { isNotFoundError } from 'src/shared/state/api-slices/engineErrors';
import { onboardingTags } from 'src/shared/state/api-slices/organisationEngineSlice';
import type { CreateOrganisationRequest, Organisation } from 'src/shared/types/engine/organisation';

// Re-exported for the GNR code that still imports them from here.
export { isConflictError, isFetchError, isNotFoundError } from 'src/shared/state/api-slices/engineErrors';
export type { Organisation } from 'src/shared/types/engine/organisation';

export type ArchiveOrganisationOutcome = 'archived' | 'none';

export type PurgeOrganisationOutcome = 'purged' | 'none';

export type RenameOrganisationOutcome = 'renamed' | 'none';

export type RenameOrganisationRequest = { bcId: string; name: string };

/** What a purge of a Nature Risk organisation would remove. */
export type PurgePreview = {
    sites: number;
    geographies: number;
    activities: number;
    risk_insights_runs: number;
};

/** Addresses the setting of one product for one organisation. */
export type ProductLicensingRequest = {
    bcId: string;
    product: ProductKey;
};

export type SetProductVisibilityRequest = ProductLicensingRequest & {
    visibility: ProductVisibility;
};

/** The read and both verbs answer with every setting of the organisation. */
export type ProductLicensingResponse = {
    product_visibility: ProductVisibilityMatrix;
};

export const getProductLicensingQuery = (bcId: string) => ({
    url: `/org/${bcId}/products`,
    method: 'GET' as const,
});

/**
 * The engine's 404 means it has no organisation for this customer, which is an
 * answer (not onboarded), not a failure, so it arrives as `null`.
 *
 * `product_visibility` falls back to no settings because nothing checks this
 * type against the wire, and the sidebar reads it on every page, where a throw
 * would take the whole app down rather than one view.
 */
export const productLicensingFrom = (result: {
    data?: unknown;
    error?: FetchBaseQueryError;
}): { data: ProductVisibilityMatrix | null } | { error: FetchBaseQueryError } => {
    if (result.error) {
        return isNotFoundError(result.error) ? { data: null } : { error: result.error };
    }
    return { data: (result.data as Partial<ProductLicensingResponse> | undefined)?.product_visibility ?? {} };
};

export const setProductVisibilityQuery = ({ bcId, product, visibility }: SetProductVisibilityRequest) => ({
    url: `/org/${bcId}/products/${product}`,
    method: 'PUT' as const,
    body: { visibility },
});

export const clearProductVisibilityQuery = ({ bcId, product }: ProductLicensingRequest) => ({
    url: `/org/${bcId}/products/${product}`,
    method: 'DELETE' as const,
});

export type SiteStatus = 'draft' | 'active' | 'archived';

export type Site = {
    id: string;
    organisation_id: string;
    name: string;
    business_central_id: string | null;
    status: SiteStatus;
    valid_from: string;
    valid_to: string | null;
    created_at: string;
    activity: { isic_code: string; isic_description: string | null } | null;
};

export type ListSitesResponse = {
    items: Site[];
    pagination: PaginationMeta;
};

/** The site's own fields. On their own they are a draft site
 *  (`POST /sites/draft`); with a geography and an activity they are a complete
 *  one (`POST /sites`, via `CreateCompleteSiteBody`). Exported so the commit
 *  pipelines can target this shape directly without duplicating the names. */
export type CreateSiteBody = {
    name: string;
    business_central_id?: string;
};

type CreateSiteRequest = {
    bcId: string;
    body: CreateSiteBody;
};

/** Builds the RTK Query request descriptor for `POST /org/{bcId}/sites/draft`
 *  — a site row on its own, with no geography and no activity.
 *
 *  `POST /org/{bcId}/sites` still accepts a bare body today, but only so
 *  clients can migrate off it; once onboarding sends the whole site in one
 *  call, that endpoint requires the geography and the activity and this is
 *  the only way to create a draft. Callers that genuinely want a bare site
 *  (project import, the manual create form) belong here.
 *
 *  Exported so the request shape can be unit-tested without running it
 *  through the RTK Query middleware. */
export const createDraftSiteQuery = ({ bcId, body }: CreateSiteRequest) => ({
    url: `/org/${bcId}/sites/draft`,
    method: 'POST' as const,
    body,
});

/** Wire shape for `POST /org/{bcId}/sites` — a whole site in one request.
 *  The engine writes the site row, its geography and its activity inside a
 *  single transaction, so a failure on any of the three leaves none of them
 *  behind. Supply all three or use `createDraftSiteQuery`: a half-filled body
 *  is a 400. */
export type CreateCompleteSiteBody = CreateSiteBody & {
    geography: GeographyFeatureBody;
    activity: SetActivityBody;
};

export type CreateCompleteSiteRequest = {
    bcId: string;
    body: CreateCompleteSiteBody;
};

/** The engine's 201 body for a complete site. Deliberately not `Site`: the
 *  `activity` here is the full SCD2 row the engine just wrote, whereas
 *  `Site.activity` is the `{isic_code, isic_description}` projection the list
 *  endpoints serve. */
export type CompleteSite = Omit<Site, 'activity'> & {
    geography: CanonicalGeographyFeature;
    activity: Activity;
};

/** Builds the RTK Query request descriptor for `POST /org/{bcId}/sites`.
 *  Exported so the request shape can be unit-tested without running it
 *  through the RTK Query middleware. */
export const createCompleteSiteQuery = ({ bcId, body }: CreateCompleteSiteRequest) => ({
    url: `/org/${bcId}/sites`,
    method: 'POST' as const,
    body,
});

type ListSitesRequest = {
    bcId: string;
    status?: SiteStatus;
};

export type GetSiteRequest = {
    bcId: string;
    siteId: string;
};

/** Wire shape for `PUT /org/{bcId}/sites/{siteId}`. `business_central_id: null`
 *  clears the field. */
export type UpdateSiteBody = {
    name: string;
    business_central_id?: string | null;
};

export type UpdateSiteRequest = {
    bcId: string;
    siteId: string;
    body: UpdateSiteBody;
};

export type UpdateSiteValidityRequest = {
    bcId: string;
    siteId: string;
    body: {
        valid_from: string;
        valid_to?: string | null;
    };
};

export type DeleteSiteRequest = {
    bcId: string;
    siteId: string;
};

export type PurgeSiteRequest = {
    bcId: string;
    siteId: string;
};

/**
 * Bulk-import feature shape sent by the Go GraphQL → engine dissolve path.
 * ADR-012 covers the merge semantics (dissolve → Polygon if connected,
 * MultiPolygon if disjoint).
 */
export type PolygonFeature = {
    type: 'Feature';
    geometry: { type: 'Polygon'; coordinates: number[][][] };
    properties: Record<string, unknown>;
};

/**
 * Input to `POST /geography/from-geojson`. Structurally guaranteed to be
 * GeoJSON (callers run the value through `check()` from
 * `@placemarkio/check-geojson` first). The engine is authoritative on which
 * of those GeoJSON shapes it will actually accept — see ADR-002 for the
 * client-vs-engine validation split.
 */
export type ConvertGeoJSONToGeographyInput = GeoJSON;

/**
 * Engine response: the canonical write-ready Feature. Same shape that the
 * geography write endpoint accepts as its body — the FE chains
 * `convertGeoJSONToGeography → createGeography` with no translation. Note this is the bare
 * Feature: the SCD2 fields (id, site_id, valid_from/_to, created_at) are
 * stamped on by the write endpoint, not present in the convertGeoJSONToGeography response.
 */
export type CanonicalGeographyGeometry =
    | { type: 'Polygon'; coordinates: number[][][] }
    | { type: 'MultiPolygon'; coordinates: number[][][][] }
    | { type: 'Point'; coordinates: number[] };
export type CanonicalGeographyFeature = {
    type: 'Feature';
    geometry: CanonicalGeographyGeometry;
    properties: Record<string, unknown>;
};

/** Builds the RTK Query request descriptor for the engine's stateless
 *  `POST /geography/from-geojson` endpoint. Exported so the request shape
 *  can be unit-tested directly without running it through the RTK Query
 *  middleware. */
export const convertGeoJSONToGeographyQuery = (body: ConvertGeoJSONToGeographyInput) => ({
    url: '/geography/from-geojson',
    method: 'POST' as const,
    body,
});

// Geography types
export type SiteGeography = {
    type: 'Feature';
    id: string;
    geometry: CanonicalGeographyGeometry;
    properties: {
        site_id: string;
        valid_from: string;
        valid_to: string | null;
        created_at: string;
    };
};

type GeographySiteRequest = {
    bcId: string;
    siteId: string;
};

/** The Feature body both geography write paths accept — the standalone
 *  `POST .../geography` and the `geography` leg of a complete site. */
export type GeographyFeatureBody = { type: 'Feature'; geometry: CanonicalGeographyGeometry; properties?: Record<string, unknown> };

type CreateGeographyRequest = {
    bcId: string;
    siteId: string;
    body: GeographyFeatureBody;
};

export type UpdateGeographyGeometryRequest = {
    bcId: string;
    siteId: string;
    geographyId: string;
    body: GeographyFeatureBody;
};

/** Builds the RTK Query request descriptor for the engine's in-place geometry
 *  correction endpoint (`PUT /org/{bcId}/sites/{siteId}/geographies/{geographyId}`).
 *  Semantics per ADR-004a: this overwrites the geometry on the existing SCD2
 *  row (correcting a previously-stored wrong geometry) rather than minting a
 *  new version. Exported so the request shape can be unit-tested without
 *  running through the RTK Query middleware. */
export const updateGeographyGeometryQuery = ({ bcId, siteId, geographyId, body }: UpdateGeographyGeometryRequest) => ({
    url: `/org/${bcId}/sites/${siteId}/geographies/${geographyId}`,
    method: 'PUT' as const,
    body,
});

export type PatchGeographyValidityRequest = {
    bcId: string;
    siteId: string;
    geographyId: string;
    body: { valid_from: string; valid_to?: string | null };
};

type RemoveGeographyVersionRequest = {
    bcId: string;
    siteId: string;
    geographyId: string;
};

// Activity types
export type Activity = {
    id: string;
    site_id: string;
    isic_code: string;
    isic_description: string | null;
    valid_from: string;
    valid_to: string | null;
    created_at: string;
};

type ActivitySiteRequest = {
    bcId: string;
    siteId: string;
};

/** Wire shape for `PUT /org/{bcId}/sites/{siteId}/activity`. */
export type SetActivityBody = { isic_code: string };

type SetActivityRequest = {
    bcId: string;
    siteId: string;
    body: SetActivityBody;
};

export type PatchActivityValidityRequest = {
    bcId: string;
    siteId: string;
    activityId: string;
    body: { valid_from: string; valid_to?: string | null };
};

type RemoveActivityVersionRequest = {
    bcId: string;
    siteId: string;
    activityId: string;
};

// ISIC Code types
export type IsicCodeEntry = {
    code: string;
    level: string;
    description: string;
    created_at: string;
};

type ListIsicCodesResponse = {
    items: IsicCodeEntry[];
    pagination: PaginationMeta;
};

// Shared pagination type
export type PaginationMeta = {
    pageSize: number;
    currentPage: number;
    totalItems: number;
    totalPages: number;
};

// List endpoints for historical versions
export type ListGeographiesRequest = {
    bcId: string;
    siteId: string;
};

export type ListGeographiesResponse = {
    items: SiteGeography[];
    pagination: PaginationMeta;
};

export type ListActivitiesRequest = {
    bcId: string;
    siteId: string;
};

export type ListActivitiesResponse = {
    items: Activity[];
    pagination: PaginationMeta;
};

// Site manifest export
export type ExportSiteManifestCSVRequest = {
    bcId: string;
};

// Site manifest preview
export type PreviewSiteManifestRequest = {
    bcId: string;
    csv: string;
};

export type PreviewSummary = {
    new: number;
    update: number;
    unchanged: number;
    errors: number;
    total: number;
};

/**
 * Field-level diff returned by the engine on `update` rows. Each variant maps
 * 1:1 to a commit-side write the frontend fires (site_name + project_code →
 * updateSite; latitude OR longitude → createGeography; isic_code →
 * setActivity). Lat/lng are reported per axis so the preview can highlight
 * the exact changed cell — at commit time both still funnel through one
 * createGeography call.
 */
export type PreviewChangedField = 'site_name' | 'project_code' | 'latitude' | 'longitude' | 'isic_code';

/**
 * The set of `field` values the engine currently emits on a row-level
 * validation error. Documentation only — `PreviewRowFieldError.field` stays
 * `string` so a future engine addition is silently ignored at the overlay
 * layer instead of crashing the preview. `coordinates` is a pseudo-field
 * used when both lat AND lng are blank-but-required (single message instead
 * of two); `internal_id` is reported only on unrecognised-id errors.
 */
export type KnownPreviewErrorField = PreviewChangedField | 'coordinates' | 'internal_id';

export type PreviewRowFieldError = {
    field: string;
    message: string;
};

/**
 * Canonical column metadata sent by the engine alongside every preview
 * response. `key` is the stable machine identifier (matches the field names
 * on `cells`, `changes`, and error `field` tags); `label` is the user-facing
 * header string. The frontend renders the table off this list so it never
 * needs to maintain a parallel column registry.
 */
export type PreviewColumn = {
    key: string;
    label: string;
};

/**
 * Parsed CSV cells for a row, keyed by the column `key` (`internal_id`,
 * `site_name`, `latitude`, …). Lets the frontend render the row + per-cell
 * overlays without re-parsing the uploaded CSV.
 */
export type PreviewRowCells = Record<string, string>;

type PreviewRowBase = {
    row: number;
    cells: PreviewRowCells;
};

export type PreviewRow =
    | (PreviewRowBase & { status: 'new' })
    | (PreviewRowBase & { status: 'update'; changes: PreviewChangedField[] })
    | (PreviewRowBase & { status: 'error'; errors: PreviewRowFieldError[] });

// The engine omits unchanged rows from `rows` — only new / update / error rows
// surface in the array. Counts for all four states live on `summary`. The
// column registry is served from a separate /site-manifest/schema endpoint
// (one-time fetch, cached) rather than ride along on every preview response.
export type PreviewSiteManifestResponse = {
    summary: PreviewSummary;
    rows: PreviewRow[];
};

const organisationEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        getOrganisation: builder.query<Organisation | null, string>({
            queryFn: async (businessCentralId, _queryApi, _extraOptions, baseQuery) => {
                const result = await baseQuery({
                    url: `/org/${businessCentralId}`,
                    method: 'GET',
                });
                if (result.error) {
                    if (isNotFoundError(result.error)) {
                        return { data: null };
                    }
                    return { error: result.error };
                }
                return { data: result.data as Organisation };
            },
            providesTags: (_result, _error, bcId) => [{ type: 'organisation', id: bcId }],
        }),
        /**
         * Archives the Nature Risk organisation of a customer. Idempotent. A customer
         * that has no Nature Risk organisation gives `'none'`, not an error.
         */
        archiveOrganisation: builder.mutation<ArchiveOrganisationOutcome, string>({
            queryFn: async (businessCentralId, _queryApi, _extraOptions, baseQuery) => {
                const result = await baseQuery({ url: `/org/${businessCentralId}`, method: 'DELETE' });
                if (!result.error) {
                    return { data: 'archived' };
                }
                if (isNotFoundError(result.error)) {
                    return { data: 'none' };
                }
                return { error: result.error };
            },
        }),
        /** Idempotent. `'none'` when the customer has no Nature Risk organisation; an archived one is refused (409). */
        renameOrganisation: builder.mutation<RenameOrganisationOutcome, RenameOrganisationRequest>({
            queryFn: async ({ bcId, name }, _queryApi, _extraOptions, baseQuery) => {
                const result = await baseQuery({ url: `/org/${bcId}`, method: 'PUT', body: { name } });
                if (!result.error) {
                    return { data: 'renamed' };
                }
                if (isNotFoundError(result.error)) {
                    return { data: 'none' };
                }
                return { error: result.error };
            },
            invalidatesTags: (_result, _error, { bcId }) => [{ type: 'organisation', id: bcId }],
        }),
        /** `null` when the customer has no Nature Risk organisation. */
        getPurgePreview: builder.query<PurgePreview | null, string>({
            queryFn: async (businessCentralId, _queryApi, _extraOptions, baseQuery) => {
                const result = await baseQuery({ url: `/org/${businessCentralId}/purge-stats`, method: 'GET' });
                if (!result.error) {
                    return { data: result.data as PurgePreview };
                }
                if (isNotFoundError(result.error)) {
                    return { data: null };
                }
                return { error: result.error };
            },
        }),
        /**
         * Permanently removes an archived Nature Risk organisation with its sites, their data and
         * its runs. The engine refuses an active organisation (409). `'none'` when it has none.
         */
        purgeOrganisation: builder.mutation<PurgeOrganisationOutcome, string>({
            queryFn: async (businessCentralId, _queryApi, _extraOptions, baseQuery) => {
                const result = await baseQuery({ url: `/org/${businessCentralId}/purge`, method: 'POST' });
                if (!result.error) {
                    return { data: 'purged' };
                }
                if (isNotFoundError(result.error)) {
                    return { data: 'none' };
                }
                return { error: result.error };
            },
        }),
        /** The product settings of one organisation; `null` when the engine does not know it. */
        getProductLicensing: builder.query<ProductVisibilityMatrix | null, string>({
            queryFn: async (bcId, _queryApi, _extraOptions, baseQuery) =>
                productLicensingFrom(await baseQuery(getProductLicensingQuery(bcId))),
            providesTags: (_result, _error, bcId) => [{ type: 'productLicensing', id: bcId }],
        }),

        /** Stores the setting of one product. Idempotent. */
        setProductVisibility: builder.mutation<ProductLicensingResponse, SetProductVisibilityRequest>({
            query: setProductVisibilityQuery,
            invalidatesTags: (_result, _error, { bcId }) => [{ type: 'productLicensing', id: bcId }],
        }),

        /** Removes the setting, so the product returns to the default. Idempotent. */
        clearProductVisibility: builder.mutation<ProductLicensingResponse, ProductLicensingRequest>({
            query: clearProductVisibilityQuery,
            invalidatesTags: (_result, _error, { bcId }) => [{ type: 'productLicensing', id: bcId }],
        }),

        createOrganisation: builder.mutation<Organisation, CreateOrganisationRequest>({
            query: body => ({
                url: '/org',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => onboardingTags(arg.business_central_id),
        }),
        listSites: builder.query<ListSitesResponse, ListSitesRequest>({
            query: ({ bcId, status = 'active' }) => ({
                url: `/org/${bcId}/sites`,
                method: 'GET',
                params: { status, page_size: 100 },
            }),
            providesTags: (_result, _error, { bcId }) => [{ type: 'sites', id: bcId }],
        }),
        createCompleteSite: builder.mutation<CompleteSite, CreateCompleteSiteRequest>({
            query: createCompleteSiteQuery,
            invalidatesTags: (_result, _error, { bcId }) => [{ type: 'sites', id: bcId }],
        }),
        createDraftSite: builder.mutation<Site, CreateSiteRequest>({
            query: createDraftSiteQuery,
            invalidatesTags: (_result, _error, { bcId }) => [{ type: 'sites', id: bcId }],
        }),
        getSite: builder.query<Site, GetSiteRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { siteId }) => [{ type: 'site', id: siteId }],
        }),
        updateSite: builder.mutation<Site, UpdateSiteRequest>({
            query: ({ bcId, siteId, body }) => ({
                url: `/org/${bcId}/sites/${siteId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { siteId }) => [{ type: 'site', id: siteId }, { type: 'sites' }],
        }),
        updateSiteValidity: builder.mutation<Site, UpdateSiteValidityRequest>({
            query: ({ bcId, siteId, body }) => ({
                url: `/org/${bcId}/sites/${siteId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { bcId, siteId }) => [
                { type: 'site', id: siteId },
                { type: 'sites', id: bcId },
            ],
        }),
        deleteSite: builder.mutation<void, DeleteSiteRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { siteId }) => [{ type: 'site', id: siteId }, { type: 'sites' }],
        }),
        purgeSite: builder.mutation<void, PurgeSiteRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}/purge`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, { siteId }) => [{ type: 'site', id: siteId }, { type: 'sites' }],
        }),

        // Stateless geography utility (no org/site context, no cache invalidation)
        convertGeoJSONToGeography: builder.mutation<CanonicalGeographyFeature, ConvertGeoJSONToGeographyInput>({
            query: convertGeoJSONToGeographyQuery,
        }),

        // Geography endpoints
        getGeography: builder.query<SiteGeography, GeographySiteRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}/geography`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { siteId }) => [{ type: 'geography', id: siteId }],
        }),
        createGeography: builder.mutation<SiteGeography, CreateGeographyRequest>({
            query: ({ bcId, siteId, body }) => ({
                url: `/org/${bcId}/sites/${siteId}/geography`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'geography', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),
        updateGeographyGeometry: builder.mutation<SiteGeography, UpdateGeographyGeometryRequest>({
            query: updateGeographyGeometryQuery,
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'geography', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),
        deleteGeography: builder.mutation<void, GeographySiteRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}/geography`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'geography', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),
        patchGeographyValidity: builder.mutation<SiteGeography, PatchGeographyValidityRequest>({
            query: ({ bcId, siteId, geographyId, body }) => ({
                url: `/org/${bcId}/sites/${siteId}/geographies/${geographyId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'geography', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),

        removeGeographyVersion: builder.mutation<void, RemoveGeographyVersionRequest>({
            query: ({ bcId, siteId, geographyId }) => ({
                url: `/org/${bcId}/sites/${siteId}/geographies/${geographyId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'geography', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),

        // Activity endpoints
        getActivity: builder.query<Activity, ActivitySiteRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}/activity`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { siteId }) => [{ type: 'activity', id: siteId }],
        }),
        setActivity: builder.mutation<Activity, SetActivityRequest>({
            query: ({ bcId, siteId, body }) => ({
                url: `/org/${bcId}/sites/${siteId}/activity`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'activity', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),
        deleteActivity: builder.mutation<void, ActivitySiteRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}/activity`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'activity', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),
        patchActivityValidity: builder.mutation<Activity, PatchActivityValidityRequest>({
            query: ({ bcId, siteId, activityId, body }) => ({
                url: `/org/${bcId}/sites/${siteId}/activities/${activityId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'activity', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),

        removeActivityVersion: builder.mutation<void, RemoveActivityVersionRequest>({
            query: ({ bcId, siteId, activityId }) => ({
                url: `/org/${bcId}/sites/${siteId}/activities/${activityId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { siteId }) => [
                { type: 'activity', id: siteId },
                { type: 'site', id: siteId },
                { type: 'sites' },
            ],
        }),

        exportSiteManifest: builder.query<string, ExportSiteManifestCSVRequest>({
            query: ({ bcId }) => ({
                url: `/org/${bcId}/site-manifest`,
                method: 'GET',
                responseHandler: 'text',
            }),
        }),

        // Org-independent static schema. RTK Query caches by default so the
        // client fetches it at most once per session and reuses for every
        // preview render.
        getSiteManifestSchema: builder.query<{ columns: PreviewColumn[] }, void>({
            query: () => ({ url: '/site-manifest/schema', method: 'GET' }),
        }),

        previewSiteManifest: builder.mutation<PreviewSiteManifestResponse, PreviewSiteManifestRequest>({
            query: ({ bcId, csv }) => ({
                url: `/org/${bcId}/site-manifest/preview`,
                method: 'POST',
                headers: { 'Content-Type': 'text/csv' },
                body: csv,
            }),
        }),

        // ISIC Codes
        listIsicCodes: builder.query<ListIsicCodesResponse, { page?: number; pageSize?: number; search?: string }>({
            query: ({ page = 1, pageSize = 100, search = '' } = {}) => ({
                url: '/isic-codes',
                method: 'GET',
                params: { page, page_size: pageSize, ...(search && { search }) },
            }),
        }),

        // Historical versions (plural endpoints)
        listGeographies: builder.query<ListGeographiesResponse, ListGeographiesRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}/geographies`,
                method: 'GET',
                params: { page_size: 100 },
            }),
            providesTags: (_result, _error, { siteId }) => [{ type: 'geography', id: siteId }],
        }),
        listActivities: builder.query<ListActivitiesResponse, ListActivitiesRequest>({
            query: ({ bcId, siteId }) => ({
                url: `/org/${bcId}/sites/${siteId}/activities`,
                method: 'GET',
                params: { page_size: 100 },
            }),
            providesTags: (_result, _error, { siteId }) => [{ type: 'activity', id: siteId }],
        }),
    }),
});

export const {
    useGetOrganisationQuery,
    useGetProductLicensingQuery,
    useLazyGetProductLicensingQuery,
    useCreateOrganisationMutation,
    useSetProductVisibilityMutation,
    useClearProductVisibilityMutation,
    useArchiveOrganisationMutation,
    useRenameOrganisationMutation,
    usePurgeOrganisationMutation,
    useGetPurgePreviewQuery,
    useListSitesQuery,
    useCreateCompleteSiteMutation,
    useCreateDraftSiteMutation,
    useGetSiteQuery,
    useUpdateSiteMutation,
    useUpdateSiteValidityMutation,
    useDeleteSiteMutation,
    usePurgeSiteMutation,
    useGetGeographyQuery,
    useCreateGeographyMutation,
    useUpdateGeographyGeometryMutation,
    useDeleteGeographyMutation,
    useGetActivityQuery,
    useSetActivityMutation,
    useDeleteActivityMutation,
    useListIsicCodesQuery,
    useListGeographiesQuery,
    useListActivitiesQuery,
    usePatchGeographyValidityMutation,
    usePatchActivityValidityMutation,
    useRemoveGeographyVersionMutation,
    useRemoveActivityVersionMutation,
    useLazyExportSiteManifestQuery,
    useGetSiteManifestSchemaQuery,
    usePreviewSiteManifestMutation,
    useConvertGeoJSONToGeographyMutation,
} = organisationEngineSlice;

export default organisationEngineSlice;
